<template>
  <div>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6 bg-slate-950">
      <RatioCard
        v-for="item in ratios"
        :key="item.id"
        :ratio="item"
        @epc-update-ratio="handleUpdateRatio"
        @epc-delete-ratio="handleDeleteRatio"
      />

      <button @click="openModal" class="border-2 border-dashed border-slate-800 rounded-xl p-4 flex flex-col items-center justify-center hover:border-blue-500 hover:bg-blue-500/5 transition group">
        <div class="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center group-hover:bg-blue-500 transition">
          <span class="text-white text-xl">+</span>
        </div>
        <span class="mt-2 text-xs font-bold text-slate-500 group-hover:text-blue-400 uppercase">{{ $t('new_ratio') }}</span>
      </button>
    </div>

    <ratio-modal ref="ratioModal"
      @epc-save-ratio="handleSaveRatio"
    />
  </div>
</template>

<script>
import RatioCard from '../ratios/RatioCard.vue';
import RatioModal from '../ratios/RatioModal.vue';

import { getPictogramLayerStatuses } from '../../services/layer-statuses.service.js';
import { addRatio, updateRatio, deleteRatio, getRatios } from '../../services/layers.service.js';

export default {
  name: 'LayerRatiosConfig',

  components: {
    RatioCard,
    RatioModal,
  },

  props: {
    layer: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      item: null,
      optionsConfig: null,
      isInitializing: true,

      statuses: [],
      ratios: [],
    };
  },

  created() {
    this._loadStatuses();
    this._loadRatios();
  },

  methods: {
    async _loadStatuses() {
      try {
        const response = await getPictogramLayerStatuses(this.layer.id, { type: 'all', with_permissions: 1 });
        if( response.success ) {
          this.statuses = response.data;
        } else {
          this.$toast.open({
            message: response.message,
            type: "is-danger",
            position: "is-top-right",
          });
        }
      } catch (error) {
        console.error('Error al obtener los estados de la capa: ', error);
        this.$toast.open({
          message: error.error_msg || this.$t("assignment_update_error"),
          type: "is-danger",
          position: "is-top-right",
        });
      }
    },

    async _loadRatios() {
      try {
        const response = await getRatios(this.layer.id);
        if( response.success ) {
          this.ratios = response.ratios;
        }
        else {
          this.$toast.open({
            message: response.error,
            type: "is-danger",
            position: "is-top-right",
          });
        }
      } catch (error) {
        console.error('Error al obtener los ratios de la capa: ', error);
        this.$toast.open({
          message: error.error_msg || this.$t("assignment_update_error"),
          type: "is-danger",
          position: "is-top-right",
        });
      }
    },

    async handleSaveRatio(data) {
      try {
        let response;
        if(data.id == null) {
          response = await addRatio(this.layer.id, { ratio: data });
        } else {
          response = await updateRatio(this.layer.id, data.id, { ratio: data });
        }

        if( response.success ) {
          if(data.id == null) {
            this.ratios.push(response.ratio);
          } else {
            this.ratios = this.ratios.map(ratio => ratio.id === data.id ? response.ratio : ratio);
          }

          this.$toast.open({
            message: response.message || this.$t('ratio_saved_successfully'),
            type: "is-success",
            position: "is-top-right",
          });
        }
      } catch (error) {
        console.error('Error al guardar el ratio: ', error);
        this.$toast.open({
          message: error.error_msg || this.$t("assignment_update_error"),
          type: "is-danger",
          position: "is-top-right",
        });
      }
    },

    async handleUpdateRatio(data) {
      this.$refs.ratioModal.show({
        title: this.$t('edit_ratio'),
        statuses: this.statuses.statusProgress,
        ratio: {
          id: data.id,
          name: data.name,
          description: data.description,
          color: data.color,
          formula: {
            numerator: {
              statusId: data.formula.numerator.statusId,
              entityId: data.formula.numerator.entityId,
              entityType: data.formula.numerator.entityType,
            },
            denominator: {
              statusId: data.formula.denominator.statusId,
              entityId: data.formula.denominator.entityId,
              entityType: data.formula.denominator.entityType,
            }
          }
        }
      });
    },

    async handleDeleteRatio(data) {
      try {
        const response = await deleteRatio(this.layer.id, data.id);
        if( response.success ) {
          this.ratios = this.ratios.filter(ratio => ratio.id !== data.id);
          this.$toast.open({
            message: response.message || this.$t('ratio_deleted_successfully'),
            type: "is-success",
            position: "is-top-right",
          });
        }
      } catch (error) {
        console.error('Error al eliminar el ratio: ', error);
        this.$toast.open({
          message: error.error_msg || this.$t("assignment_update_error"),
          type: "is-danger",
          position: "is-top-right",
        });
      }
    },

    openModal() {
      this.$refs.ratioModal.show({
        title: this.$t('new_ratio'),
        statuses: this.statuses.statusProgress,
        ratio: {
          name: '',
          description: '',
          color: '#3b82f6', // Blue 500
          formula: {
            numerator: {
              statusId: null,
              entityId: null,
              entityType: 'group',
            },
            denominator: {
              statusId: null,
              entityId: null,
              entityType: 'group',
            }
          }
        }
      });
    }
  }
}
</script>
